import Axios, {AxiosInstance} from "axios";

export interface AttachmentUploadApiDto {
	id: string;
	uploadUrl: string;
}

export interface TaxiClaimPreviewApiDto {
	registration: string;
	mileage: number;
	source: string;
}

export interface TaxiClaimPreviewResponseApiDto {
	eligible: boolean;
	payoutCents: number;
}

export interface TaxiClaimSubmissionApiDto {
	registration: string;
	mileage: number;
	attachmentIds: string[];
	signatureAttachmentId: string;
	source: string;
}

export interface TaxiClaimResponseApiDto {
	jobReference: string;
	payoutCents: number;
	eligible: boolean;
}

/**
 * API client for the public taxi claim journey. It deliberately avoids the portal auth interceptors:
 * the account token is passed into each request and never persisted by this class.
 */
export default class TaxiClaimApi {
	constructor(
		public readonly axiosInstance: AxiosInstance = Axios.create({timeout: 30000}),
		public readonly uploadAxiosInstance: AxiosInstance = Axios.create({timeout: 120000})
	) {}

	createAttachment(name: string, accessToken: string): Promise<AttachmentUploadApiDto> {
		return this.axiosInstance.post(
			"/api/mobile/attachments",
			{name},
			{headers: this.authorizationHeaders(accessToken)}
		).then(response => response.data);
	}

	previewClaim(data: TaxiClaimPreviewApiDto, accessToken: string): Promise<TaxiClaimPreviewResponseApiDto> {
		return this.axiosInstance.post(
			"/api/mobile/vehicle-service/claim-preview",
			data,
			{headers: this.authorizationHeaders(accessToken)}
		).then(response => response.data);
	}

	uploadAttachment(file: Blob, name: string, accessToken: string): Promise<string> {
		return this.createAttachment(name, accessToken).then(attachment => {
			return this.uploadAxiosInstance.put(attachment.uploadUrl, file, {
				headers: {"Content-Type": file.type || "application/octet-stream"}
			}).then(() => attachment.id);
		});
	}

	submitClaim(data: TaxiClaimSubmissionApiDto, accessToken: string): Promise<TaxiClaimResponseApiDto> {
		return this.axiosInstance.post(
			"/api/mobile/vehicle-service/jobs",
			data,
			{headers: this.authorizationHeaders(accessToken)}
		).then(response => response.data);
	}

	private authorizationHeaders(accessToken: string): {[header: string]: string} {
		return {Authorization: `Bearer ${accessToken}`};
	}
}

export const taxiClaimApi = new TaxiClaimApi();
